'use client';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { CalendarIcon, CreditCard, Repeat, Hash, Clock, Settings, DollarSign, Zap } from 'lucide-react';
import { SubscriptionDetail } from '@/lib/api/subscriptions';
import { formatMoney } from "@/lib/money";
import { DateDisplay } from '@/components/date-display';
import { useTranslation } from 'react-i18next';
import { Separator } from '@/components/ui/separator';
import { SubscriptionStatusBadge } from '@/components/subscriptions/subscription-status-badge';
import { CopyableResourceBadge } from '@/components/ui/copyable-resource-badge';
import { SubscriptionResourceBadge } from '../../components/SubscriptionResourceBadge';
import { ProductResourceBadge } from '@/components/products/ProductResourceBadge';
import { ProcessorResourceBadge } from '@/components/processor/ProcessorRessourceBadge';
import { OrderResourceBadge } from '@/components/orders/OrderResourceBadge';

interface SubscriptionDetailsTabProps {
  subscription: SubscriptionDetail;
}

export function SubscriptionDetailsTab({ subscription }: SubscriptionDetailsTabProps) {
  const { t } = useTranslation('subscriptions');
  const currency = subscription.currency || 'USD';
  const amount = subscription.priceSnapshot?.currencyOptions?.[currency]?.amount || 0;

  return (
    <div className="space-y-6">
      {/* Basic Information */}
      <Card>
        <CardHeader>
          <CardTitle>{t('details')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Price Information */}
          <div className="space-y-4">
            <h3 className="flex items-center gap-2 font-semibold">
              <DollarSign className="h-4 w-4" />
              {t('priceInformation')}
            </h3>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <div className="text-sm font-medium">{t('priceId')}</div>
                <CopyableResourceBadge
                  name={t('priceId')}
                  value={subscription.priceId || 'N/A'}
                  className="font-mono text-xs"
                />
              </div>
              <div className="space-y-2">
                <div className="text-sm font-medium">{t('productId')}</div>
                {subscription.productId && (
                  <ProductResourceBadge productId={subscription.productId} />
                )}
                {!subscription.productId && (
                  <div className="text-muted-foreground font-mono text-sm">
                    {subscription.productId || 'N/A'}
                  </div>
                )}
              </div>
            </div>

            {/* Multi-currency pricing */}
            {(subscription as any).priceCurrencyOptions &&
              Object.keys((subscription as any).priceCurrencyOptions).length > 0 && (
                <div className="space-y-3">
                  <div className="text-sm font-medium">{t('currencyOptions')}</div>
                  <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
                    {Object.entries((subscription as any).priceCurrencyOptions).map(
                      ([currencyCode, priceAmount]) => (
                        <div
                          key={currencyCode}
                          className="flex items-center justify-between rounded-md border p-2"
                        >
                          <span className="text-sm font-medium">{currencyCode}</span>
                          <span className="text-sm">{formatMoney(priceAmount as number, currencyCode)}</span>
                        </div>
                      ),
                    )}
                  </div>
                </div>
              )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent>{/* Billing Cycle Information */}

          <div className="space-y-4">
            <h3 className="flex items-center gap-2 font-semibold">
              <CalendarIcon className="h-4 w-4" />
              {t('billingInformation')}
            </h3>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <div className="text-sm font-medium">{t('billingCycle')}</div>
                <div className="text-sm">
                  {subscription.currentPeriodStart && subscription.currentPeriodEnd ? (
                    <>
                      <DateDisplay value={subscription.currentPeriodStart} mode="date" /> -{' '}
                      <DateDisplay value={subscription.currentPeriodEnd} mode="date" />
                    </>
                  ) : (
                    'N/A - N/A'
                  )}
                </div>
              </div>
              <div className="space-y-2">
                <div className="text-sm font-medium">{t('nextBillingDate')}</div>
                <div className="text-sm">
                  <DateDisplay value={subscription.nextBillingDate} />
                </div>
              </div>
              <div className="space-y-2">
                <div className="text-sm font-medium">{t('billingCycleAnchor')}</div>
                <div className="text-sm">
                  <DateDisplay value={subscription.billingCycleAnchor} />
                </div>
              </div>
              <div className="space-y-2">
                <div className="text-sm font-medium">{t('quantity')}</div>
                <div className="text-sm">{subscription.quantity || 1}</div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardContent>
          {/* Payment Processor Information */}
          <div className="space-y-4">
            <h3 className="flex items-center gap-2 font-semibold">
              <Settings className="h-4 w-4" />
              {t('paymentProcessor')}
            </h3>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <div className="text-sm font-medium">{t('paymentFlow')}</div>
                <div className="text-sm flex items-center gap-2">
                  {subscription.preferredProcessor?.name || t('default')}
                  {subscription.preferredProcessorId && (
                    <ProcessorResourceBadge
                      processorId={subscription.preferredProcessorId}
                    />
                  )}
                </div>
                <div className="text-muted-foreground text-xs">
                  {subscription.preferredProcessor?.name
                    ? t('usesProcessorName', { name: subscription.preferredProcessor.name })
                    : t('usesStorePaymentFlowConfig')}

                </div>

              </div>
              {subscription.defaultPaymentInstrumentId && (
                <div className="space-y-2">
                  <div className="text-sm font-medium">{t('paymentMethod')}</div>
                  <div className="text-sm">{t('paymentMethodSet')}</div>
                  <div className="text-muted-foreground font-mono text-xs">
                    {subscription.defaultPaymentInstrumentId}
                  </div>
                  <div className="text-muted-foreground text-xs">
                    {t('subscriptionUsesSpecificPaymentMethod')}
                  </div>
                </div>
              )}
              {(subscription as any).useTagadaVault && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm font-medium">
                    <Zap className="h-4 w-4" />
                    {t('tagadaVault')}
                  </div>
                  <div className="text-sm text-green-600">{t('enabled')}</div>
                  <div className="text-muted-foreground text-xs">{t('usingTagadaSecurePaymentVault')}</div>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardContent>
          {(subscription.trialStart || subscription.trialEnd) && (
            <div className="space-y-4">
              <h3 className="font-semibold">{t('trialInformation')}</h3>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm font-medium">
                    <CalendarIcon className="h-4 w-4" />
                    {t('trialStart')}
                  </div>
                  <div className="text-sm">
                    <DateDisplay value={subscription.trialStart} mode="date" />
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm font-medium">
                    <CalendarIcon className="h-4 w-4" />
                    {t('trialEnd')}
                  </div>
                  <div className="text-sm">
                    <DateDisplay value={subscription.trialEnd} mode="date" />
                  </div>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
      <Card>
        <CardContent>
          {/* Additional Details */}
          <div className="space-y-4">
            <h3 className="flex items-center gap-2 font-semibold">
              <Hash className="h-4 w-4" />
              {t('additionalDetails')}
            </h3>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <div className="text-sm font-medium">{t('primaryCurrency')}</div>
                <div className="text-sm font-semibold">{currency}</div>
              </div>
              {subscription.orderId && (
                <div className="space-y-2">
                  <div className="text-sm font-medium">{t('relatedOrder')}</div>
                  <OrderResourceBadge orderId={subscription.orderId} />
                </div>
              )}
              <div className="space-y-2">
                <div className="text-sm font-medium">{t('createdAt')}</div>
                <div className="text-sm">
                  <DateDisplay value={subscription.createdAt} fallback={t('unknown')} />
                </div>
              </div>
              <div className="space-y-2">
                <div className="text-sm font-medium">{t('updatedAt')}</div>
                <div className="text-sm">
                  <DateDisplay value={subscription.updatedAt} fallback={t('unknown')} />
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
